import * as React from 'react';
import type { CalendarEvent } from '../../../MedicareProAPI/Entities/CalendarEvent';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import { UserAvatar } from '../User/UserAvatar';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import useScrollTo from '../../Effect/ScrollTo';

export interface CalendarDayProps {
  events?: CalendarEvent[];
  users?: User[];
  dateTime?: Date;
  onEdit?: (calendarEvent: CalendarEvent) => void;
}

export interface CalendarEventUserGroup {
  events?: CalendarEvent[];
  user: User;
}

export const CalendarDay = (props: CalendarDayProps): React.JSX.Element => {
  const [dateTimeStart, setDateTimeStart] = React.useState<Date>();
  const [events, setEvents] = React.useState<CalendarEventUserGroup[]>();
  const [users, setUsers] = React.useState<User[]>();

  const hoursInDay = 24;
  const hours = Array.from(Array(hoursInDay).keys());

  const date = new Date();
  const hourNow = date.getHours();

  // Handle auto scroll for day view
  const dayScrollRef = React.useRef<HTMLDivElement | null>(null);
  useScrollTo(dayScrollRef, { xCoord: 167 * 6 });

  React.useEffect(() => {
    const userEventsGroup: CalendarEventUserGroup[] = [];

    if (props.events) {
      props.events.forEach((calendarEventSingle) => {
        let existingGroup = userEventsGroup.find(
          (item) => item.user.getUserId() === calendarEventSingle.getUser().getUserId()
        );

        if (!existingGroup) {
          existingGroup = {
            user: calendarEventSingle.getUser(),
            events: [],
          };

          userEventsGroup.push(existingGroup);
        }

        existingGroup.events.push(calendarEventSingle);
      });

      setEvents(userEventsGroup);
    }
  }, [props.events]);

  React.useEffect(() => {
    if (props.dateTime) {
      setDateTimeStart(props.dateTime);
    }
  }, [props.dateTime]);

  React.useEffect(() => {
    setUsers(props.users);
  }, [props.users]);

  return (
    <div className="calendar-day-outer" ref={dayScrollRef}>
      <div className="calendar-day-container">
        <div className="day-hour-container">
          {hours.map((hour) => (
            <div className="day-hour p-2 small" key={hour}>
              <span
                style={{ fontSize: '11pt' }}
                className={`${hourNow == hour ? 'text-danger font-weight-bold' : ''}`}
              >
                {Utilities.renderAMPM(hour)}
              </span>
              <span className={`ml-1 ${hourNow == hour ? 'text-danger font-weight-bold' : ''}`}>
                {Utilities.renderAMPMString(hour)}
              </span>
            </div>
          ))}
        </div>
        {events &&
          users &&
          users.map((user) => {
            const userEventGroup = events.find(
              (eventSingle) => eventSingle.user.getUserId() === user.getUserId()
            );

            return (
              <div
                key={user.getUserId()}
                className="pt-1 cp"
                style={{ borderTop: '1px solid rgba(0, 0, 0, 0.2)', minHeight: '58px' }}
              >
                {users.length > 1 && (
                  <UserAvatar
                    style={{ position: 'absolute', left: 0 }}
                    className="ml-2 mt-1"
                    user={user}
                  />
                )}
                {userEventGroup &&
                  userEventGroup.events &&
                  userEventGroup.events.map((eventSingle) => {
                    const hoursWidth = 4.16666666667;
                    const dateTimeStart = eventSingle.getDateTimeStart();
                    const dateTimeEnd = eventSingle.getDateTimeEnd();

                    const offset =
                      ((dateTimeStart.getHours() * 60 + dateTimeStart.getMinutes()) / 60) *
                      hoursWidth;
                    const width =
                      ((dateTimeEnd.getTime() - dateTimeStart.getTime()) / 1000 / 60 / 60) *
                      hoursWidth;

                    return (
                      <div
                        style={{ marginLeft: offset + '%', width: width + '%' }}
                        className="badge badge-sm border badge-primary border-primary mb-1 text-left"
                        onClick={(e) => props.onEdit(eventSingle)}
                        key={eventSingle.getId()}
                      >
                        {eventSingle.getName()}
                        {eventSingle.getInds() && eventSingle.getInds().length > 0 && (
                          <>
                            <br />
                            <em className="font-weight-normal">
                              with
                              {eventSingle.getInds().map((ind, index) => {
                                return (
                                  ind.getIndName() +
                                  (eventSingle.getInds().length > 1 &&
                                  index + 1 !== eventSingle.getInds().length
                                    ? ', '
                                    : '')
                                );
                              })}
                            </em>
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
      </div>
    </div>
  );
};
